import { useEffect, useState } from 'react'
import App from 'next/app'
import { DefaultSeo } from 'next-seo'
import NProgress from '~/components/NProgress'
import { useRouter } from 'next/router'
import { ToastProvider } from '@ticketswap/solar'
import BaseStyles from '~/components/BaseStyles'
import { setTrackingIdentity, page, pageSource } from '~/utils/analytics'
import LoggedInUserProvider from '~/components/LoggedInUserContext'
import ConsentDialog from '~/components/ConsentDialog/dynamic'
import NetworkStatus from '~/components/NetworkStatus'
import createSeoConfig from '~/lib/next-seo-defaults'
import { withTranslation, appWithTranslation } from '~/i18n'
import { withServerResponse } from '~/components/ServerResponseContext'
import getConfig from 'next/config'
import LocaleBar from '~/components/LocaleBar/dynamic'
import getLoggedInUser from '~/lib/get-logged-in-user'
import { NativeBridge } from '~/utils/nativeBridge'
import { LanguageProvider } from '~/components/LanguageContext'
import getDeviceInfo from '~/utils/getDeviceInfo'
import { decodeGlobalId } from '~/utils/id'
import CookieToast from '~/components/CookieToast/dynamic'
import { setFlow, FlowProvider } from '~/components/FlowContext'
import { IntercomProvider } from '~/components/IntercomProvider'
import FacebookProvider from '~/components/FacebookContext'
import { initializeApollo, useApollo } from '~/lib/apolloClient'
import { ApolloProvider } from '@apollo/client'
import LocaleBarProvider from '~/components/LocaleBarContext'
import { getLanguageCodeByLocale } from '~/utils/localization'
import Referrals from '~/components/Referrals'

const { FACEBOOK_APP_ID, USER_ID_COOKIE_NAME, INTERCOM_APP_ID } = getConfig().publicRuntimeConfig

const MyApp = ({
  Component,
  loggedInUser,
  deviceInfo,
  pageProps,
  t,
  flow,
  i18n,
  openGraphUrl,
  currentUrl,
}) => {
  const router = useRouter()
  const apolloClient = useApollo({ flow }, pageProps.initialApolloState)
  const seoDescription = t('common:site.description')
  const locale = router.locale || i18n.language || 'en'
  const language = getLanguageCodeByLocale(locale)
  const [tracked, setTracked] = useState(false)

  useEffect(() => {
    const handleRouteChange = path => {
      if (global.newrelic) {
        global.newrelic.setCurrentRouteName(window.next.router.pathname)
        global.newrelic.setPageViewName(window.next.router.pathname)
      }

      page(path)
    }

    router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router.events])

  useEffect(() => {
    if (!tracked) {
      setTracked(true)
      loggedInUser && setTrackingIdentity(loggedInUser, false)

      // Track initial page-view
      page(router.asPath)

      if (global.newrelic) {
        global.newrelic.setCurrentRouteName(router.pathname)
      }

      // Track referrer-info once per session
      pageSource(router.asPath)
    }
  }, [router.asPath, tracked, loggedInUser])

  return (
    <ApolloProvider client={apolloClient}>
      <LanguageProvider value={language}>
        <FlowProvider value={flow}>
          <NProgress />
          <BaseStyles />
          <NativeBridge />
          <DefaultSeo {...createSeoConfig(seoDescription, openGraphUrl)} />
          <FacebookProvider appId={FACEBOOK_APP_ID}>
            <LoggedInUserProvider data={{ deviceInfo, currentUrl }}>
              <IntercomProvider appId={INTERCOM_APP_ID}>
                <ToastProvider>
                  <LocaleBarProvider language={language}>
                    <Referrals />
                    <ConsentDialog />
                    <LocaleBar />
                    <NetworkStatus />
                    <Component {...pageProps} />
                    {!deviceInfo.isApp && <CookieToast t={t} />}
                  </LocaleBarProvider>
                </ToastProvider>
              </IntercomProvider>
            </LoggedInUserProvider>
          </FacebookProvider>
        </FlowProvider>
      </LanguageProvider>
    </ApolloProvider>
  )
}

MyApp.getInitialProps = async appContext => {
  const { ctx, Component } = appContext
  const isBrowser = typeof window !== 'undefined'

  /**
   * Copy the components flow into the context so it can be forwarded to
   * requests and errors reporting later.
   */
  ctx.flow = Component.WrappedComponent?.flow || Component.flow || ctx.req?.query?.flow

  if (isBrowser) {
    setFlow(ctx.flow)
  }

  const appProps = await App.getInitialProps(appContext)
  const apolloClient = initializeApollo(ctx, appProps.pageProps.initialApolloState)

  if (ctx.req && ctx.req.newrelic) {
    ctx.req.newrelic.setTransactionName(ctx.pathname)
    ctx.req.newrelic.addCustomAttribute('flow', ctx.flow)
  }

  const deviceInfo = getDeviceInfo(ctx.req) || {}
  const loggedInUser = await getLoggedInUser(apolloClient, ctx.req)

  if (loggedInUser && ctx.res && !ctx.res.headersSent) {
    /**
     * Only set cookie if the headers are not send yet.
     * This problem is because when redirecting a page nextJS will still render the DOM.
     * This will be fixes after redirect is implemented in getServerSideProps.
     */
    ctx.res.cookie(USER_ID_COOKIE_NAME, decodeGlobalId(loggedInUser.id), {
      secure: true,
      sameSite: 'lax',
    })
  }

  const currentUrl = ctx?.req
    ? `${ctx.req.headers['x-forwarded-proto']}://${ctx.req.get('host')}`
    : `${window.location.protocol}//${window.location.host}`
  const openGraphUrl = ctx?.req
    ? `${currentUrl}${ctx.req.originalUrl}`
    : `${currentUrl}${window.location.pathname}`

  appProps.pageProps.initialApolloState = apolloClient.cache.extract()

  return {
    loggedInUser,
    deviceInfo,
    flow: ctx.flow,
    openGraphUrl,
    currentUrl,
    ...appProps,
  }
}

export default appWithTranslation(withServerResponse(withTranslation(['common'])(MyApp)))
